/**
 * Renders image-plane scene objects as textured 1×1 planes.
 *
 * The texture comes from the local IndexedDB reference-image store
 * (`referenceImageDB`). The plane is double-sided, alpha-tested for PNG
 * cutout transparency, and unlit — image planes are reference aids, not
 * shaded geometry. They are excluded from Edit / Sculpt / Sketch modes.
 *
 * Sizing: spawn-time scale bakes in the image aspect (largest side = 1u),
 * so the 1×1 base geometry combined with `obj.scale` reproduces the
 * intrinsic aspect. Subsequent uniform scale by the user preserves it.
 */

import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three/webgpu";
import {
  applySceneObjectWorldTransformToObject3D,
  getSceneObjectWorldTransform,
} from "../app/collectionGraph.ts";
import { getReferenceImageUrl } from "../app/persistence/referenceImageDB.ts";
import { useStore } from "../app/store.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import { isVisibleInWorkspace } from "../app/visibilityPolicy.ts";
import type { SceneObject } from "../modeling/types.ts";
import {
  HIDDEN_SELECTION_GHOST_OPACITY,
  shouldShowHiddenSelectionGhost,
} from "./hiddenSelectionGhost.ts";
import { useObjectTap } from "./useObjectTap.ts";
import { viewportSupportsSceneInteraction } from "./viewportInteractionPolicy.ts";

// One unit-square plane geometry shared across every ImagePlaneNode — the
// per-object aspect/size lives in `SceneObject.scale`, not in the geometry.
const SHARED_PLANE_GEOMETRY = new THREE.PlaneGeometry(1, 1);

// Border edge geometry — paired vertices for `THREE.LineSegments`. Four
// edges, one segment each, traced ccw from bottom-left. Geometry lives in
// the same XY plane as `PlaneGeometry` so the border lines up with the
// textured surface under any subsequent rotation.
const SHARED_BORDER_GEOMETRY = (() => {
  const g = new THREE.BufferGeometry();
  const corners: Array<[number, number]> = [
    [-0.5, -0.5],
    [0.5, -0.5],
    [0.5, 0.5],
    [-0.5, 0.5],
  ];
  const positions = new Float32Array(corners.length * 2 * 3);
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    positions[i * 6 + 0] = a[0];
    positions[i * 6 + 1] = a[1];
    positions[i * 6 + 2] = 0;
    positions[i * 6 + 3] = b[0];
    positions[i * 6 + 4] = b[1];
    positions[i * 6 + 5] = 0;
  }
  g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return g;
})();

const SELECTED_BORDER_COLOR = new THREE.Color("#f59e0b");
const UNSELECTED_BORDER_COLOR = new THREE.Color("#888888");

export function ImagePlaneNode({ obj }: { obj: SceneObject }) {
  const selected = useSelectedLeafObjectIds().has(obj.id);
  const visible = useStore((s) => s.visibility.get(obj.id) ?? true);
  const renderVisible = useStore((s) => s.renderVisibility.get(obj.id) ?? true);
  const workspaceMode = useStore((s) => s.workspaceMode);
  const showSceneInteraction = viewportSupportsSceneInteraction(workspaceMode);
  const effectiveVisible = isVisibleInWorkspace({
    workspaceMode,
    viewportVisible: visible,
    renderVisible,
  });
  const showHiddenSelectionGhost = shouldShowHiddenSelectionGhost({
    selected,
    effectiveVisible,
    showSceneInteraction,
  });
  const renderVisibleInViewport = effectiveVisible || showHiddenSelectionGhost;

  const groupRef = useRef<THREE.Group>(null);
  const objId = obj.id;
  const imageKey = obj.imagePlane?.imageKey ?? null;

  // Snapshot pattern matches every other scene-object node: read parent-chain
  // world transform once via `useStore.getState()` with deps only on the
  // object's own TRS. The per-frame `useFrame` below keeps the rendered
  // group current under parent collection drags.
  const worldTransform = useMemo(
    () =>
      getSceneObjectWorldTransform(
        useStore.getState().sceneObjects,
        useStore.getState().collections,
        obj.id,
      ) ?? {
        position: obj.position,
        rotation: obj.rotation,
        scale: obj.scale,
      },
    [obj.id, obj.position, obj.rotation, obj.scale],
  );

  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const latestState = useStore.getState();
    applySceneObjectWorldTransformToObject3D(
      latestState.sceneObjects,
      latestState.collections,
      objId,
      g,
    );
  });

  const {
    handlePointerDown,
    handlePointerUp,
    handlePointerEnter,
    handlePointerLeave,
  } = useObjectTap(obj.id);

  // Texture loading — pulls the compressed WebP blob from local IndexedDB
  // and wraps it in a THREE.Texture. The blob URL and texture are revoked
  // on unmount or when the imageKey changes.
  const [texture, setTexture] = useState<THREE.Texture | null>(null);

  useEffect(() => {
    if (!imageKey) {
      setTexture(null);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    let createdTex: THREE.Texture | null = null;

    (async () => {
      const url = await getReferenceImageUrl(imageKey);
      if (cancelled || !url) return;
      createdUrl = url;
      const img = new Image();
      img.src = url;
      try {
        await img.decode();
      } catch {
        if (!cancelled && createdUrl) URL.revokeObjectURL(createdUrl);
        return;
      }
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      const tex = new THREE.Texture(img);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 8;
      tex.needsUpdate = true;
      createdTex = tex;
      setTexture(tex);
    })();

    return () => {
      cancelled = true;
      if (createdTex) createdTex.dispose();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setTexture(null);
    };
  }, [imageKey]);

  // Surface material — unlit, double-sided, alpha-tested. Alpha test (vs
  // alpha blend) avoids transparency sort artifacts when stacking image
  // planes against scene geometry.
  const material = useMemo(() => {
    const m = new THREE.MeshBasicMaterial({
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.01,
      toneMapped: false,
      map: null,
    });
    return m;
  }, []);

  useEffect(() => {
    material.map = texture;
    material.opacity = showHiddenSelectionGhost
      ? HIDDEN_SELECTION_GHOST_OPACITY
      : 1;
    material.needsUpdate = true;
  }, [material, texture, showHiddenSelectionGhost]);

  // Dispose the material when the component unmounts. The geometry is
  // module-shared so it must NOT be disposed here.
  useEffect(() => {
    return () => {
      material.dispose();
    };
  }, [material]);

  const borderMaterial = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: selected ? SELECTED_BORDER_COLOR : UNSELECTED_BORDER_COLOR,
        transparent: true,
        opacity: selected ? 1 : 0.45,
        toneMapped: false,
        depthTest: !showHiddenSelectionGhost,
      }),
    [selected, showHiddenSelectionGhost],
  );

  useEffect(() => {
    return () => {
      borderMaterial.dispose();
    };
  }, [borderMaterial]);

  // Aspect is baked into an inner group rather than the SceneObject's scale,
  // so the Transform panel shows clean 1.0 defaults (Blender-style "applied"
  // scale). Largest side = 1 world unit; the other side carries the aspect
  // ratio. Falls back to a square when intrinsic dimensions are missing.
  const aspectScale = useMemo<[number, number, number]>(() => {
    const ip = obj.imagePlane;
    if (!ip) return [1, 1, 1];
    const w = Math.max(1, ip.intrinsicWidth);
    const h = Math.max(1, ip.intrinsicHeight);
    if (w >= h) return [1, h / w, 1];
    return [w / h, 1, 1];
  }, [obj.imagePlane]);

  if (!renderVisibleInViewport) return null;

  return (
    <group
      ref={groupRef}
      position={worldTransform.position}
      rotation={worldTransform.rotation as unknown as THREE.Euler}
      scale={worldTransform.scale}
      name={obj.id}
    >
      <group scale={aspectScale}>
        <mesh
          geometry={SHARED_PLANE_GEOMETRY}
          material={material}
          onPointerDown={showSceneInteraction ? handlePointerDown : undefined}
          onPointerUp={showSceneInteraction ? handlePointerUp : undefined}
          onPointerEnter={showSceneInteraction ? handlePointerEnter : undefined}
          onPointerLeave={showSceneInteraction ? handlePointerLeave : undefined}
        />
        <lineSegments
          geometry={SHARED_BORDER_GEOMETRY}
          material={borderMaterial}
          renderOrder={999}
        />
      </group>
    </group>
  );
}
